import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Message,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from '@arco-design/web-react';
import { adminWebhooksApi } from '@/api';
import type { PaginatedResult } from '@/types/api';
import type {
  WebhookInboxDetail,
  WebhookInboxItem,
  WebhookInboxQuery,
  WebhookProcessStatus,
} from '@/types/webhook';

const STATUS_COLOR: Record<WebhookProcessStatus, string> = {
  PENDING: 'blue',
  PROCESSED: 'green',
  FAILED: 'red',
  IGNORED: 'gray',
};

const EVENT_TYPES = [
  'add_card',
  'writeoff',
  'refill',
  'transaction',
  'verification_code',
  'freezecard',
];

export default function WebhooksView() {
  const [data, setData] = useState<PaginatedResult<WebhookInboxItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<WebhookInboxQuery>({ page: 1, pageSize: 20 });
  const [detail, setDetail] = useState<WebhookInboxDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminWebhooksApi
      .list(query)
      .then(setData)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const d = await adminWebhooksApi.detail(id);
      setDetail(d);
    } finally {
      setDetailLoading(false);
    }
  };

  const reprocess = async (id: string) => {
    await adminWebhooksApi.reprocess(id);
    Message.success('已重新派发处理');
    load();
    if (detail?.id === id) openDetail(id);
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <div className="admin-page__title">Webhook 收件箱</div>
          <div className="admin-page__subtitle">
            上游 Newgen 异步通知与本地处理状态（add_card / writeoff / refill / transaction 等）
          </div>
        </div>
      </div>

      <div className="admin-card">
        <Form
          layout="inline"
          style={{ marginBottom: 12 }}
          initialValues={query}
          onSubmit={(values) => setQuery({ ...query, ...values, page: 1 })}
        >
          <Form.Item field="eventType" label="事件类型">
            <Select placeholder="全部" allowClear style={{ width: 180 }}>
              {EVENT_TYPES.map((t) => (
                <Select.Option key={t} value={t}>
                  {t}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item field="processStatus" label="处理状态">
            <Select placeholder="全部" allowClear style={{ width: 160 }}>
              {(['PENDING', 'PROCESSED', 'FAILED', 'IGNORED'] as WebhookProcessStatus[]).map(
                (s) => (
                  <Select.Option key={s} value={s}>
                    {s}
                  </Select.Option>
                ),
              )}
            </Select>
          </Form.Item>
          <Form.Item field="keyword" label="单号">
            <Input placeholder="out_trade_no / card_id" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                查询
              </Button>
              <Button onClick={() => setQuery({ page: 1, pageSize: 20 })}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <Table<WebhookInboxItem>
          loading={loading}
          rowKey="id"
          data={data?.items ?? []}
          pagination={{
            current: query.page ?? 1,
            pageSize: query.pageSize ?? 20,
            total: data?.total ?? 0,
            onChange: (p) => setQuery({ ...query, page: p }),
            showTotal: true,
          }}
          columns={[
            { title: '接收时间', dataIndex: 'receivedAt' },
            { title: '事件类型', dataIndex: 'eventType' },
            {
              title: '商户单号',
              dataIndex: 'outTradeNo',
              render: (v) => v || '-',
            },
            {
              title: '卡 ID',
              dataIndex: 'cardId',
              render: (v) => (v ? <span className="admin-mono">{v}</span> : '-'),
            },
            {
              title: '签名',
              dataIndex: 'signatureValid',
              render: (v: boolean) =>
                v ? <Tag color="green">通过</Tag> : <Tag color="red">失败</Tag>,
            },
            {
              title: '处理状态',
              dataIndex: 'processStatus',
              render: (v: WebhookProcessStatus) => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
            },
            { title: '重试次数', dataIndex: 'retryCount', align: 'right' },
            {
              title: '操作',
              dataIndex: 'op',
              width: 180,
              render: (_, row) => (
                <Space>
                  <Button size="mini" onClick={() => openDetail(row.id)}>
                    详情
                  </Button>
                  {(row.processStatus === 'FAILED' || row.processStatus === 'PENDING') && (
                    <Button size="mini" type="primary" onClick={() => reprocess(row.id)}>
                      重新处理
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </div>

      <Drawer
        title="Webhook 详情"
        width={640}
        visible={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
      >
        {detailLoading || !detail ? (
          <Typography.Text type="secondary">加载中…</Typography.Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <div>
              <Tag color={STATUS_COLOR[detail.processStatus]}>{detail.processStatus}</Tag>{' '}
              <Tag>{detail.eventType}</Tag>{' '}
              {detail.signatureValid ? (
                <Tag color="green">签名通过</Tag>
              ) : (
                <Tag color="red">签名失败</Tag>
              )}
            </div>
            <Typography.Text type="secondary">
              接收时间：{detail.receivedAt} · 处理时间：{detail.processedAt ?? '-'} · 重试：
              {detail.retryCount}
            </Typography.Text>
            {detail.processMessage && (
              <div>
                <div style={{ marginBottom: 4, color: 'var(--admin-text-tertiary)' }}>
                  处理消息：
                </div>
                <pre
                  style={{
                    background: 'var(--admin-bg-page)',
                    padding: 12,
                    borderRadius: 6,
                    margin: 0,
                  }}
                >
                  {detail.processMessage}
                </pre>
              </div>
            )}
            <div>
              <div style={{ marginBottom: 4, color: 'var(--admin-text-tertiary)' }}>
                原始 payload：
              </div>
              <pre
                style={{
                  background: 'var(--admin-bg-page)',
                  padding: 12,
                  borderRadius: 6,
                  margin: 0,
                  maxHeight: 360,
                  overflow: 'auto',
                }}
              >
                {typeof detail.payload === 'string'
                  ? detail.payload
                  : JSON.stringify(detail.payload, null, 2)}
              </pre>
            </div>
          </Space>
        )}
      </Drawer>
    </div>
  );
}
